import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
  computed,
  input,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { ReplaySubject, map, takeUntil, distinctUntilChanged, filter, startWith, pairwise } from 'rxjs';

import { coreSelectors } from 'app/core/state';
import { mediaSelectors } from '../../state/media';
import { Loggable } from 'app/utils/logging/loggable';
import User from 'entity/User';

import { ModalVideoEffectsComponent } from '../../modal-video-effects/modal-video-effects.component';
import { BrowserService } from '../../services/browser.service';
import { DeviceService } from '../../services/device/device.service';
import { DevicePermissionService } from '../../services/device-permission/device-permission.service';
import { PermissionsService } from '../../services/permissions.service';
import { ConnectedRoomCounts, socialAppSelectors } from '../../state/app';
import { notNil } from 'app/utils/stream-util';
import Social from 'entity/Social';
import { ConnectedRoomsService } from '../../services/connected-rooms.service';

// Interface to represent all device permission service state in one place
interface DeviceState {
  videoEnabled: boolean;
  micEnabled: boolean;
  audioGranted: boolean;
  videoGranted: boolean;
  cameraButtonDisabled: boolean;
  showAvatarVideo: boolean;
  showAvatarPicture: boolean;
  videoEffectsDisabled: boolean;
}

@Component({
  selector: 'app-welcome-preview',
  templateUrl: './welcome-preview.component.html',
  styleUrls: ['./welcome-preview.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WelcomePreviewComponent extends Loggable implements OnInit, OnDestroy {
  private dialog = inject(MatDialog);
  private deviceService = inject(DeviceService);
  private permissionsService = inject(PermissionsService);
  private devicePermissionService = inject(DevicePermissionService);
  private browserService = inject(BrowserService);
  private store = inject(Store);
  private cdr = inject(ChangeDetectorRef);
  private connectedRoomsService = inject(ConnectedRoomsService);

  public readonly user = input<User>();
  public readonly showSmartBadging = input<boolean>();
  public readonly smartBadgingHasQuestions = input<boolean>();
  public readonly showWelcomeDeviceSelectionForm = input<boolean>();
  public readonly showOrientationStep = input<boolean>();
  public readonly canUserJoin = input(false);

  private destroyed$ = new ReplaySubject<boolean>(1);

  public readonly socialStarted = signal<boolean>(false);
  public readonly selectedRoom = signal<string | undefined>(undefined);
  private readonly connectedRoomCounts = signal<ConnectedRoomCounts | null>(null);
  public readonly connectedRoomSet = signal<Social[]>([]);
  public defaultSelected = 'default';

  // Badge preview signals
  protected readonly companyName = signal<string>('');
  protected readonly userName = signal<string>('');
  protected readonly userTitle = signal<string>('');
  protected readonly aboutMe = signal<string>('');

  // Single consolidated device state signal
  private readonly deviceState = signal<DeviceState>({
    videoEnabled: false,
    micEnabled: false,
    audioGranted: false,
    videoGranted: false,
    cameraButtonDisabled: false,
    showAvatarVideo: false,
    showAvatarPicture: false,
    videoEffectsDisabled: false,
  });

  // Computed signals derived from the consolidated device state
  public readonly shouldShowAvatarVideo = computed(() => this.deviceState().showAvatarVideo);
  public readonly shouldShowAvatarPicture = computed(() => this.deviceState().showAvatarPicture);
  public readonly disableCameraButton = computed(
    () => this.deviceState().videoGranted && this.deviceState().cameraButtonDisabled,
  );
  public readonly disableVideoEffects = computed(
    () => !this.deviceService.initialized || this.deviceState().videoEffectsDisabled,
  );

  // Computed signals for UI indicators
  public readonly showVideoEnabledIcon = computed(
    () => this.deviceState().videoEnabled && this.deviceState().videoGranted,
  );

  public readonly showMicEnabledIcon = computed(() => this.deviceState().micEnabled && this.deviceState().audioGranted);

  // Safari-specific styling
  protected readonly safariStyle = computed(() => (this.browserService.isSafari ? { height: 'auto' } : undefined));

  public constructor() {
    super();
    this.setDisplayName('WelcomePermissionsComponent');
  }

  public ngOnInit(): void {
    // Initialize state from service
    this.updateDeviceState();

    // Set up subscriptions for state changes
    this.setupStateSubscriptions();

    // Set up user profile subscription
    this.setupUserProfileSubscription();
  }

  /**
   * Toggle functions
   */
  public clickedToggleVideo(): void {
    const state = this.deviceState();

    if (!state.cameraButtonDisabled) {
      if (!state.videoGranted) {
        this.devicePermissionService.showExplanationModal();
        this.permissionsService.requestVideoPermission();
      } else {
        this.devicePermissionService.setVideoState();
      }
    }
  }

  public clickedToggleMic(): void {
    const state = this.deviceState();

    if (!state.audioGranted) {
      this.devicePermissionService.showExplanationModal();
      this.permissionsService.requestAudioPermission();
    } else {
      this.devicePermissionService.setAudioState();
    }
  }

  // Convenience getters for template usage
  public get isVideoEnabled(): boolean {
    return this.deviceState().videoEnabled;
  }

  public get isMicEnabled(): boolean {
    return this.deviceState().micEnabled;
  }

  public get audioPermissionGranted(): boolean {
    return this.deviceState().audioGranted;
  }

  public get videoPermissionGranted(): boolean {
    return this.deviceState().videoGranted;
  }

  public onVideoPlaying = (): void => {
    this.devicePermissionService.onVideoPlaying();
    this.updateDeviceState(); // Explicitly update state when video starts playing
  };

  public onVideoTimeout = (): void => {
    this.devicePermissionService.onVideoTimeout();
  };

  public handleVideoEffectsClick(): void {
    this.logUserAction('video-effects', 'click');
    this.dialog.open<ModalVideoEffectsComponent>(ModalVideoEffectsComponent);
  }

  /**
   * Set up observable subscriptions for state changes
   */
  private setupStateSubscriptions(): void {
    // Subscribe to video enabled state
    this.store
      .select(mediaSelectors.isVideoEnabled)
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe(() => this.updateDeviceState());

    // Subscribe to mic enabled state
    this.store
      .select(mediaSelectors.isMicEnabled)
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe(() => this.updateDeviceState());

    // Create an observable for permission changes
    this.permissionsService.didUpdatePermission
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.updateDeviceState());

    // Subscribe to camera changes
    this.store
      .select(mediaSelectors.getSelectedCamera)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.updateDeviceState());

    this.store
      .select(socialAppSelectors.getCurrentConnectedRoomSet)
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe((connectedRoomSet) => this.connectedRoomSet.set(connectedRoomSet.socials ?? []));

    this.store
      .select(socialAppSelectors.getCurrentSocial)
      .pipe(filter(notNil), startWith({} as Social), pairwise(), takeUntil(this.destroyed$))
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .subscribe(([_prevSocial, currentSocial]) => this.selectedRoom.set(currentSocial.socialId));

    this.store
      .select(socialAppSelectors.getCurrentConnectedRoomCounts)
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe((updatedCounts: ConnectedRoomCounts) => this.connectedRoomCounts.set(updatedCounts));
  }

  /**
   * Setup subscription for user profile data
   */
  private setupUserProfileSubscription(): void {
    this.store
      .select(coreSelectors.getUserAndProfileAndAccountInfo)
      .pipe(
        map(({ company, user, userProfile }) => ({
          companyName: company?.companyName,
          title: userProfile?.title,
          aboutMe: userProfile?.description,
          firstName: user.user?.firstName,
          lastName: user.user?.lastName,
        })),
        takeUntil(this.destroyed$),
      )
      .subscribe(({ aboutMe, companyName, firstName, lastName, title }) => {
        this.companyName.set(companyName ?? '');
        this.userTitle.set(title ?? '');
        this.aboutMe.set(aboutMe ?? '');
        this.userName.set(this.formatUserName(firstName, lastName));
      });
  }

  /**
   * Update the consolidated device state from services
   */
  private updateDeviceState(): void {
    const devicePermission = this.devicePermissionService;
    const permission = devicePermission.permission;

    this.deviceState.update((state) => ({
      ...state,
      videoEnabled: devicePermission.isVideoEnabled(),
      micEnabled: devicePermission.isMicEnabled(),
      audioGranted: permission()?.audioGranted() ?? false,
      videoGranted: permission()?.videoGranted() ?? false,
      cameraButtonDisabled: devicePermission.shouldCameraButtonDisabled(),
      showAvatarVideo: devicePermission.shouldShowAvatarVideo(),
      showAvatarPicture: devicePermission.shouldShowAvatarPicture(),
      videoEffectsDisabled: devicePermission.shouldDisableVideoEffects(),
    }));
  }

  /* destroy calls */
  public async ngOnDestroy(): Promise<void> {
    try {
      await this.permissionsService.cleanAllStreams();
    } catch (e) {
      this.error('Error cleaning up streams:', e);
    }
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public formatUserName(firstName?: string, lastName?: string): string {
    return lastName ? `${firstName} ${lastName}` : `${firstName}`;
  }

  //Room selection methods
  public get hideRoomSelectionInput(): boolean {
    return this.connectedRoomsService.dropdownDisabled() || !this.connectedRoomsService.isConnected();
  }

  public isConnectedRoomFull(room: Social): boolean {
    const roomCount = this.getRoomCount(room) ?? 0;
    const maxParticipants = room.maxParticipants ?? 0;
    return roomCount > maxParticipants - 1;
  }

  public getRoomCount(room: Social): number | undefined {
    if (!this.connectedRoomCounts()) {
      return;
    }
    const { socialId } = room ?? {};
    if (!socialId) {
      return 0;
    }
    return this.connectedRoomCounts()?.[socialId] ?? 0;
  }

  public getRoomPopulationText(room: Social): string {
    const actualPop = (this.getRoomCount(room) || 0).toString();
    const totalPop = (room.maxParticipants || 0).toString();
    return `${actualPop}/${totalPop}`;
  }

  public onRoomChange({ socialId }: Social): void {
    this.connectedRoomsService.onRoomChange({ socialId });
  }
}
